import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  TextField,
  Paper,
  Typography,
  useTheme,
  useMediaQuery,
  Button,
  CircularProgress,
} from "@mui/material";
import MyLocationIcon from "@mui/icons-material/MyLocation";
import PushPinIcon from '@mui/icons-material/PushPin';
import TipsAndUpdatesIcon from '@mui/icons-material/TipsAndUpdates';
import {
  MapContainer,
  TileLayer,
  useMapEvents,
} from "react-leaflet";
import L from "leaflet";
import LocationSearch from "./LocationSearch";
import { SelectedLocation } from "../../models/LocationSearch";
import {useStore } from "../../stores/store";

// Fix Leaflet default icon
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "",
  iconUrl: "",
  shadowUrl: "",
});

interface MapLocationPickerProps {
  latitude: string;
  longitude: string;
  onLatitudeChange: (value: string) => void;
  onLongitudeChange: (value: string) => void;
  label?: string;
}

// Map updater component - only handles initial map load
interface MapUpdaterProps {
  center: [number, number];
  zoom: number;
}

const MapUpdater: React.FC<MapUpdaterProps> = ({ center, zoom }) => {
  const map = useMapEvents({
    load: () => {
      map.setView(center, zoom);
    },
  });

  return null;
};

// Map drag end handler - updates coordinates when user drags the map
interface MapDragHandlerProps {
  onMapMove: (lat: number, lng: number) => void;
}

const MapDragHandler: React.FC<MapDragHandlerProps> = ({ onMapMove }) => {
  useMapEvents({
    dragend: (e) => {
      const center = e.target.getCenter();
      onMapMove(center.lat, center.lng);
    },
    zoomend: (e) => {
      const center = e.target.getCenter();
      onMapMove(center.lat, center.lng);
    },
  });
  return null;
};

const MapLocationPicker: React.FC<MapLocationPickerProps> = ({
  latitude,
  longitude,
  onLatitudeChange,
  onLongitudeChange,
  label = "Select Location",
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const mapRef = useRef<any>(null);
  const { toasterStore } = useStore();

  const [mapCenter, setMapCenter] = useState<[number, number]>(
    latitude && longitude ? [parseFloat(latitude), parseFloat(longitude)] : [28.6016, 77.3895] // Deliverit location
  );
  const [mapZoom, setMapZoom] = useState<number>(15);
  const [isLoadingLocation, setIsLoadingLocation] = useState(false);
  const [selectedAddress, setSelectedAddress] = useState<string>("");

  // Get current location from browser
  const handleUseCurrentLocation = () => {
    setIsLoadingLocation(true);
    
    if (!navigator.geolocation) {
      toasterStore.error("Geolocation is not supported by your browser");
      setIsLoadingLocation(false);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude: lat, longitude: lng } = position.coords;
        onLatitudeChange(lat.toFixed(6));
        onLongitudeChange(lng.toFixed(6));
        setMapCenter([lat, lng]);
        setMapZoom(16); // Zoom in to show current location detail
        setSelectedAddress("Current Location"); // Show that current location was selected
        
        // Update map view immediately
        if (mapRef.current) {
          mapRef.current.setView([lat, lng], 16);
        }
        setIsLoadingLocation(false);
      },
      (error) => {
        console.error("Geolocation error:", error);
        let errorMessage = "Unable to get your location";
        if (error.code === 1) {
          errorMessage = "Permission denied. Please enable location access.";
        } else if (error.code === 2) {
          errorMessage = "Position unavailable. Please try again.";
        } else if (error.code === 3) {
          errorMessage = "Request timed out. Please try again.";
        }
        toasterStore.error(errorMessage);
        setIsLoadingLocation(false);
      }
    );
  };

  // Handle map movement
  const handleMapMove = (lat: number, lng: number) => {
    onLatitudeChange(lat.toFixed(6));
    onLongitudeChange(lng.toFixed(6));
    setMapCenter([lat, lng]);
  };

  // Update map center when latitude or longitude change from parent
  useEffect(() => {
    if (latitude && longitude) {
      const newLat = parseFloat(latitude);
      const newLng = parseFloat(longitude);
      if (!isNaN(newLat) && !isNaN(newLng)) {
        setMapCenter([newLat, newLng]);
      }
    }
  }, [latitude, longitude]);

  // Handle location selected from search
  const handleLocationSelected = (location: SelectedLocation) => {
    if (location.latitude && location.longitude) {
      onLatitudeChange(location.latitude.toFixed(6));
      onLongitudeChange(location.longitude.toFixed(6));
      setMapCenter([location.latitude, location.longitude]);
      setMapZoom(18); // Zoom in when location is selected for better detail
      
      // Update map view immediately
      if (mapRef.current) {
        mapRef.current.setView([location.latitude, location.longitude], 18);
      }
      
      // Build and set selected address
      const fullAddress = `${location.mainText}, ${location.secondaryText}`;
      setSelectedAddress(fullAddress);
    } else {
      console.error("Location missing latitude or longitude:", location);
    }
  };

  return (
    <Box
      sx={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
        gap: 2,
        position: "relative",
      }}
    >
      <Box sx={{ position: "relative", zIndex: 1200 }}>
        <LocationSearch
          onLocationSelected={handleLocationSelected}
          label="Search Location"
          placeholder="Type location name to search..."
        />
      </Box>

      {/* Header Section */}
      <Paper
        elevation={0}
        sx={{
          background: "rgba(255, 255, 255, 0.95)",
          backdropFilter: "blur(10px)",
          borderRadius: 3,
          p: 2,
          boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1)",
          transition: "transform 0.3s ease, box-shadow 0.3s ease",
          "&:hover": {
            transform: "translateY(-2px)",
            boxShadow: "0 12px 40px rgba(0, 0, 0, 0.15)",
          },
        }}
      >
        <Typography
          variant="h6"
          fontWeight={600}
          color="text.primary"
          sx={{
            mb: 2,
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <PushPinIcon /> {label}
        </Typography>

        {/* Coordinates Input Fields */}
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 2,
          }}
        >
          <Box
            sx={{
              flex: { xs: "1 1 100%", sm: "1 1 calc(50% - 8px)" },
              minWidth: 0,
            }}
          >
            <TextField
              fullWidth
              label="Latitude"
              type="number"
              disabled
              inputProps={{
                step: "0.000001",
                min: "-90",
                max: "90",
              }}
              value={latitude}
              onChange={(e) => {
                onLatitudeChange(e.target.value);
                if (longitude) {
                  setMapCenter([
                    parseFloat(e.target.value) || mapCenter[0],
                    parseFloat(longitude) || mapCenter[1],
                  ]);
                }
              }}
              required
            />
          </Box>
          <Box
            sx={{
              flex: { xs: "1 1 100%", sm: "1 1 calc(50% - 8px)" },
              minWidth: 0,
            }}
          >
            <TextField
              fullWidth
              label="Longitude"
              type="number"
              disabled
              inputProps={{
                step: "0.000001",
                min: "-180",
                max: "180",
              }}
              value={longitude}
              onChange={(e) => {
                onLongitudeChange(e.target.value);
                if (latitude) {
                  setMapCenter([
                    parseFloat(latitude) || mapCenter[0],
                    parseFloat(e.target.value) || mapCenter[1],
                  ]);
                }
              }}
              required
            />
          </Box>
        </Box>

        <Typography
          variant="caption"
          sx={{
            display: "flex",
            alignItems: "center",
            mt: 1.5,
            color: "green",
            fontStyle: "italic",
            gap: 0.5,
            textAlign: "center",
          }}
        >
          <TipsAndUpdatesIcon fontSize="small" />
          Drag the map to select location. Pin stays in center.
        </Typography>
      </Paper>

      {/* Map Container */}
      <Paper
        elevation={0}
        sx={{
          background: "rgba(255, 255, 255, 0.95)",
          backdropFilter: "blur(10px)",
          borderRadius: 3,
          overflow: "hidden",
          boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1)",
          transition: "transform 0.3s ease, box-shadow 0.3s ease",
          height: isSmallScreen ? 350 : isMobile ? 450 : 550,
          position: "relative",
          "&:hover": {
            transform: "translateY(-2px)",
            boxShadow: "0 12px 40px rgba(0, 0, 0, 0.15)",
          },
        }}
      >
        <MapContainer
          center={mapCenter}
          zoom={mapZoom}
          style={{ height: "100%", width: "100%" }}
          ref={mapRef}
          dragging={true}
          touchZoom={true}
          zoomControl={true}
        >
          {/* Map update handler */}
          <MapUpdater center={mapCenter} zoom={mapZoom} />

          {/* Map drag handler */}
          <MapDragHandler onMapMove={handleMapMove} />

          {/* Google Maps style */}
          <TileLayer
            url="http://mt1.google.com/vt/lyrs=m&x={x}&y={y}&z={z}"
            attribution='&copy; <a href="https://maps.google.com">Google Maps</a>'
            maxZoom={18}
          />
        </MapContainer>

        {/* Fixed Center Pin */}
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -100%)",
            pointerEvents: "none",
            zIndex: 1000,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 0.5,
          }}
        >
          {/* Pin SVG */}
          <svg
            width="40"
            height="50"
            viewBox="0 0 24 32"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M12 0C5.4 0 0 5.4 0 12c0 9 12 20 12 20s12-11 12-20c0-6.6-5.4-12-12-12z"
              fill="#FF3B30"
            />
            <circle cx="12" cy="12" r="5" fill="white" />
          </svg>
          {/* Subtle pulse animation */}
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: "50%",
              backgroundColor: "#FF3B30",
              animation: "pulse 2s infinite",
              "@keyframes pulse": {
                "0%": { opacity: 1, transform: "scale(1)" },
                "50%": { opacity: 0.7 },
                "100%": { opacity: 1, transform: "scale(1)" },
              },
            }}
          />
        </Box>

        {/* Use Current Location Button - Bottom Center */}
        <Button
          variant="contained"
          size="medium"
          startIcon={
            isLoadingLocation ? (
              <CircularProgress size={18} color="inherit" />
            ) : (
              <MyLocationIcon />
            )
          }
          onClick={handleUseCurrentLocation}
          disabled={isLoadingLocation}
          sx={{
            position: "absolute",
            bottom: 20,
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 1000,
            pointerEvents: "auto",
            textTransform: "none",
            fontWeight: 600,
            backgroundColor: "#1976d2",
            color: "white",
            padding: { xs: "10px", md: "8px 20px" },
            borderRadius: "24px",
            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.2)",
            minWidth: { xs: "44px", md: "auto" },
            minHeight: { xs: "44px", md: "auto" },
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            "& .MuiButton-startIcon": {
              marginRight: { xs: 0, md: "8px" },
            },
            "&:hover": {
              backgroundColor: "#1565c0",
              boxShadow: "0 6px 16px rgba(0, 0, 0, 0.3)",
            },
            "&:disabled": {
              backgroundColor: "#ccc",
              color: "#666",
            },
          }}
        >
          <span style={{ display: isMobile ? "none" : "inline" }}>
            {isLoadingLocation ? "Getting Location..." : "Use Current Location"}
          </span>
        </Button>
      </Paper>

      {/* Selected Address Display */}
      {selectedAddress && (
        <Box
          sx={{
            padding: "12px 16px",
            backgroundColor: "#f0fdf4",
            border: "1px solid #22c55e",
            borderRadius: "8px",
            marginTop: 1,
          }}
        >
          <Typography
            variant="caption"
            sx={{
              color: "#16a34a",
              fontWeight: 500,
              display: "block",
              mb: 0.5,
            }}
          >
             Selected Address:
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: "#15803d",
              fontWeight: 500,
              wordBreak: "break-word",
            }}
          >
            {selectedAddress}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default MapLocationPicker;
